import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const dashboardItems = [
  {
    label: '总分趋势',
    desc: '一所学校整体总分分布随时间的变化',
    icon: 'ri-line-chart-line',
    color: 'primary' as const,
    chart: 'line',
    metric: '+23%',
    metricLabel: '同比提升',
  },
  {
    label: '分档比例',
    desc: '优秀、良好、合格、需重点支持的比例',
    icon: 'ri-pie-chart-line',
    color: 'accent' as const,
    chart: 'pie',
    metric: '4档',
    metricLabel: '分层统计',
  },
  {
    label: '主题覆盖',
    desc: '哪些课程主题覆盖充分，哪些薄弱',
    icon: 'ri-radar-line',
    color: 'primary' as const,
    chart: 'radar',
    metric: '85%',
    metricLabel: '覆盖率',
  },
  {
    label: '分布热力图',
    desc: '一校一图、一省一图的质量结构可视化',
    icon: 'ri-map-2-line',
    color: 'accent' as const,
    chart: 'heatmap',
    metric: '6维',
    metricLabel: '多维对比',
  },
  {
    label: '教师成长',
    desc: '哪些教师成长明显，哪些教研组需支持',
    icon: 'ri-user-star-line',
    color: 'primary' as const,
    chart: 'bar',
    metric: '18人',
    metricLabel: '成长明显',
  },
  {
    label: '资源投向',
    desc: '基于数据的资源配置优化建议',
    icon: 'ri-funds-line',
    color: 'accent' as const,
    chart: 'arrow',
    metric: 'AI',
    metricLabel: '智能推荐',
  },
];

// 鲜艳硬编码颜色，彻底摆脱灰色调
const VIVID = {
  red: '#EF4444',
  orange: '#F97316',
  amber: '#F59E0B',
  yellow: '#EAB308',
  lime: '#84CC16',
  green: '#22C55E',
  emerald: '#10B981',
  teal: '#14B8A6',
  cyan: '#06B6D4',
  sky: '#0EA5E9',
  blue: '#3B82F6',
  indigo: '#6366F1',
  violet: '#8B5CF6',
  purple: '#A855F7',
  fuchsia: '#D946EF',
  pink: '#EC4899',
  rose: '#FB7185',
  slate: '#64748B',
  slateL: '#94A3B8',
  slateXL: '#CBD5E1',
};

function MiniLineChart() {
  const points = [
    { x: 15, y: 82, c: VIVID.red },
    { x: 65, y: 72, c: VIVID.orange },
    { x: 115, y: 58, c: VIVID.amber },
    { x: 165, y: 42, c: VIVID.green },
    { x: 215, y: 28, c: VIVID.teal },
    { x: 265, y: 14, c: VIVID.blue },
  ];
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const areaD = `${pathD} L265,90 L15,90 Z`;
  return (
    <svg viewBox="0 0 280 100" className="w-full h-24">
      <defs>
        <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={VIVID.blue} stopOpacity="0.35" />
          <stop offset="100%" stopColor={VIVID.blue} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {/* 背景网格 */}
      {[20, 40, 60, 80].map((y) => (
        <line key={y} x1="0" y1={y} x2="280" y2={y} stroke="#E2E8F0" strokeWidth="0.5" />
      ))}
      {/* 面积填充 */}
      <path d={areaD} fill="url(#lineGrad)" />
      {/* 折线 */}
      <path d={pathD} fill="none" stroke={VIVID.blue} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* 数据点 */}
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="6" fill={p.c} fillOpacity="0.15" />
          <circle cx={p.x} cy={p.y} r="3.5" fill="white" stroke={p.c} strokeWidth="2.5" />
        </g>
      ))}
      {/* 终点高亮 */}
      <circle cx="265" cy="14" r="5" fill={VIVID.blue} />
      <circle cx="265" cy="14" r="9" fill={VIVID.blue} fillOpacity="0.12" />
    </svg>
  );
}

function MiniPieChart() {
  const colors = [VIVID.green, VIVID.amber, VIVID.orange, VIVID.red];
  const r = 32;
  const circ = 2 * Math.PI * r;
  const shares = [0.35, 0.30, 0.20, 0.15];
  let offset = 0;
  return (
    <svg viewBox="0 0 200 100" className="w-full h-24">
      <g transform="translate(70, 50)">
        {/* 背景灰圈 */}
        <circle r={r} fill="none" stroke="#E2E8F0" strokeWidth="12" />
        {shares.map((share, i) => {
          const dash = `${circ * share} ${circ}`;
          const off = -offset * circ;
          offset += share;
          return (
            <circle
              key={i}
              r={r}
              fill="none"
              stroke={colors[i]}
              strokeWidth="12"
              strokeDasharray={dash}
              strokeDashoffset={off}
              strokeLinecap="butt"
            />
          );
        })}
        <text y="4" textAnchor="middle" fontSize="13" fill="#334155" fontWeight="bold">4</text>
        <text y="18" textAnchor="middle" fontSize="8" fill="#94A3B8">档位</text>
      </g>
      {/* 图例 */}
      {[
        { label: '优秀', color: colors[0], pct: '35%' },
        { label: '良好', color: colors[1], pct: '30%' },
        { label: '合格', color: colors[2], pct: '20%' },
        { label: '需支持', color: colors[3], pct: '15%' },
      ].map((item, i) => (
        <g key={item.label} transform={`translate(125, ${20 + i * 18})`}>
          <rect width="10" height="10" rx="3" fill={item.color} />
          <text x="16" y="8.5" fontSize="9" fill="#475569">{item.label}</text>
          <text x="16" y="17" fontSize="8" fill={item.color} fontWeight="600">{item.pct}</text>
        </g>
      ))}
    </svg>
  );
}

function MiniRadarChart() {
  const data = [
    { label: '思政', val: 0.85, color: VIVID.red },
    { label: '案例', val: 0.72, color: VIVID.orange },
    { label: '互动', val: 0.90, color: VIVID.amber },
    { label: '逻辑', val: 0.78, color: VIVID.green },
    { label: '育人', val: 0.68, color: VIVID.teal },
    { label: '效果', val: 0.82, color: VIVID.blue },
  ];
  const maxR = 38;
  const angleStep = (Math.PI * 2) / 6;
  const pts = data.map((d, i) => {
    const angle = -Math.PI / 2 + i * angleStep;
    const r = d.val * maxR;
    return { x: Math.cos(angle) * r, y: Math.sin(angle) * r };
  });
  const polygonPts = pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  return (
    <svg viewBox="0 0 220 110" className="w-full h-24">
      <g transform="translate(80, 55)">
        {/* 同心网格 */}
        {[1, 2, 3, 4].map((i) => (
          <polygon
            key={i}
            points={`${maxR * i / 4},0 ${(maxR * i / 4) * Math.cos(Math.PI / 6)},${-(maxR * i / 4) * Math.sin(Math.PI / 6)} ${-(maxR * i / 4) * Math.cos(Math.PI / 6)},${-(maxR * i / 4) * Math.sin(Math.PI / 6)} -${maxR * i / 4},0 ${-(maxR * i / 4) * Math.cos(Math.PI / 6)},${(maxR * i / 4) * Math.sin(Math.PI / 6)} ${(maxR * i / 4) * Math.cos(Math.PI / 6)},${(maxR * i / 4) * Math.sin(Math.PI / 6)}`}
            fill="none"
            stroke="#E2E8F0"
            strokeWidth="0.8"
          />
        ))}
        {/* 轴线 */}
        {Array.from({ length: 6 }).map((_, i) => {
          const angle = -Math.PI / 2 + i * angleStep;
          const x = Math.cos(angle) * maxR;
          const y = Math.sin(angle) * maxR;
          return <line key={i} x1="0" y1="0" x2={x} y2={y} stroke="#E2E8F0" strokeWidth="0.8" />;
        })}
        {/* 数据面 */}
        <polygon points={polygonPts} fill={VIVID.blue} fillOpacity="0.12" stroke={VIVID.blue} strokeWidth="1.8" strokeLinejoin="round" />
        {/* 数据点 */}
        {pts.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="5" fill={data[i].color} fillOpacity="0.15" />
            <circle cx={p.x} cy={p.y} r="3" fill="white" stroke={data[i].color} strokeWidth="2" />
          </g>
        ))}
      </g>
      {/* 标签 */}
      {[
        ['思政', 142, 48],
        ['案例', 115, 15],
        ['互动', 52, 15],
        ['逻辑', 30, 48],
        ['育人', 52, 88],
        ['效果', 115, 88],
      ].map(([label, x, y], i) => (
        <text key={i} x={x} y={y} textAnchor="middle" fontSize="9" fill="#64748B">{label}</text>
      ))}
    </svg>
  );
}

function MiniHeatmap() {
  // 热力图标准色阶：浅蓝 → 蓝 → 绿 → 黄 → 橙 → 红
  const heatColors = [
    '#DBEAFE', '#BFDBFE', '#93C5FD', '#60A5FA', '#3B82F6',
    '#22C55E', '#84CC16', '#EAB308', '#F59E0B', '#F97316', '#EF4444',
  ];
  const values = [
    2, 3, 5, 7, 4, 6, 8, 5,
    3, 4, 6, 8, 5, 7, 9, 6,
    4, 5, 7, 9, 6, 8, 10, 7,
    5, 6, 8, 10, 7, 9, 10, 8,
  ];
  const rows = 4;
  const cols = 8;
  const w = 16;
  const h = 16;
  const gap = 4;
  const totalW = cols * (w + gap) - gap;
  const startX = (220 - totalW) / 2;
  return (
    <svg viewBox="0 0 220 100" className="w-full h-24">
      {values.map((val, i) => {
        const row = Math.floor(i / cols);
        const col = i % cols;
        const colorIdx = Math.min(Math.floor((val / 10) * (heatColors.length - 1)), heatColors.length - 1);
        return (
          <rect
            key={i}
            x={startX + col * (w + gap)}
            y={10 + row * (h + gap)}
            width={w}
            height={h}
            rx="4"
            fill={heatColors[colorIdx]}
          />
        );
      })}
      {['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月'].map((label, i) => (
        <text key={i} x={startX + i * (w + gap) + w / 2} y="88" textAnchor="middle" fontSize="8" fill="#94A3B8">{label}</text>
      ))}
      {/* 右侧色阶条 */}
      <g transform="translate(205, 10)">
        {heatColors.map((c, i) => (
          <rect key={i} x="0" y={i * 7} width="8" height="7" fill={c} />
        ))}
      </g>
    </svg>
  );
}

function MiniBarChart() {
  const bars = [
    { h: 28, color: VIVID.red, label: 'A' },
    { h: 24, color: VIVID.orange, label: 'B' },
    { h: 40, color: VIVID.amber, label: 'C' },
    { h: 34, color: VIVID.green, label: 'D' },
    { h: 50, color: VIVID.teal, label: 'E' },
    { h: 42, color: VIVID.blue, label: 'F' },
    { h: 58, color: VIVID.indigo, label: 'G' },
  ];
  const bw = 18;
  const gap = 5;
  const totalW = bars.length * (bw + gap) - gap;
  const startX = (220 - totalW) / 2;
  const baseline = 78;
  return (
    <svg viewBox="0 0 220 100" className="w-full h-24">
      {/* Y轴网格 */}
      {[20, 40, 60].map((y) => (
        <line key={y} x1="10" y1={y} x2="210" y2={y} stroke="#E2E8F0" strokeWidth="0.5" />
      ))}
      <line x1="10" y1={baseline} x2="210" y2={baseline} stroke="#CBD5E1" strokeWidth="1" />
      {/* 柱状 */}
      {bars.map((bar, i) => (
        <g key={i}>
          <rect
            x={startX + i * (bw + gap)}
            y={baseline - bar.h}
            width={bw}
            height={bar.h}
            rx="5"
            fill={bar.color}
          />
          <text
            x={startX + i * (bw + gap) + bw / 2}
            y={baseline - bar.h - 5}
            textAnchor="middle"
            fontSize="8"
            fill={bar.color}
            fontWeight="600"
          >
            {bar.h}
          </text>
        </g>
      ))}
      {/* X轴标签 */}
      {bars.map((bar, i) => (
        <text key={i} x={startX + i * (bw + gap) + bw / 2} y={baseline + 14} textAnchor="middle" fontSize="9" fill="#64748B">{bar.label}</text>
      ))}
    </svg>
  );
}

function MiniArrowFlow() {
  return (
    <svg viewBox="0 0 220 100" className="w-full h-24">
      {/* 数据节点 */}
      <rect x="10" y="25" width="52" height="50" rx="10" fill={VIVID.blue} fillOpacity="0.08" stroke={VIVID.blue} strokeWidth="2" />
      <text x="36" y="54" textAnchor="middle" fontSize="11" fill={VIVID.blue} fontWeight="bold">数据</text>

      {/* 箭头1 */}
      <path d="M68 50 L108 50 L100 42 M108 50 L100 58" stroke={VIVID.teal} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />

      {/* 分析节点 */}
      <rect x="116" y="25" width="52" height="50" rx="10" fill={VIVID.teal} fillOpacity="0.08" stroke={VIVID.teal} strokeWidth="2" />
      <text x="142" y="54" textAnchor="middle" fontSize="11" fill={VIVID.teal} fontWeight="bold">分析</text>

      {/* 箭头2 */}
      <path d="M174 50 L214 50 L206 42 M214 50 L206 58" stroke={VIVID.orange} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />

      {/* AI节点 */}
      <rect x="162" y="25" width="52" height="50" rx="10" fill={VIVID.orange} fillOpacity="0.08" stroke={VIVID.orange} strokeWidth="2" />
      <text x="188" y="54" textAnchor="middle" fontSize="11" fill={VIVID.orange} fontWeight="bold">AI</text>

      {/* 底部步骤点 */}
      <g transform="translate(36, 82)">
        <circle r="3" fill={VIVID.blue} />
        <text y="12" textAnchor="middle" fontSize="7" fill="#94A3B8">采集</text>
      </g>
      <g transform="translate(110, 82)">
        <circle r="3" fill={VIVID.teal} />
        <text y="12" textAnchor="middle" fontSize="7" fill="#94A3B8">处理</text>
      </g>
      <g transform="translate(188, 82)">
        <circle r="3" fill={VIVID.orange} />
        <text y="12" textAnchor="middle" fontSize="7" fill="#94A3B8">决策</text>
      </g>
    </svg>
  );
}

const ChartMap: Record<string, React.FC> = {
  line: MiniLineChart,
  pie: MiniPieChart,
  radar: MiniRadarChart,
  heatmap: MiniHeatmap,
  bar: MiniBarChart,
  arrow: MiniArrowFlow,
};

export default function P20EvidenceManager({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  // 每张卡片分配一个鲜艳的底色
  const cardBgColors = [
    { bg: '#EFF6FF', border: '#BFDBFE', top: VIVID.blue, hover: '#DBEAFE' },   // 蓝
    { bg: '#F0FDF4', border: '#BBF7D0', top: VIVID.green, hover: '#DCFCE7' },   // 绿
    { bg: '#FFFBEB', border: '#FDE68A', top: VIVID.amber, hover: '#FEF3C7' },   // 黄
    { bg: '#FFF1F2', border: '#FECDD3', top: VIVID.red, hover: '#FFE4E6' },      // 红
    { bg: '#F0FDFA', border: '#99F6E4', top: VIVID.teal, hover: '#CCFBF1' },   // 青
    { bg: '#F5F3FF', border: '#DDD6FE', top: VIVID.violet, hover: '#EDE9FE' }, // 紫
  ];

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-100/12 blur-[130px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-100/10 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-100/6 blur-[150px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div ref={titleRef} className="text-center mb-10 md:mb-12">
          <span className={`inline-block text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            服务管理者
          </span>
          <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            证据<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">对管理者</span>的价值
          </h2>
          <p className={`text-base text-foreground-950 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            AI提供的是质量趋势和资源配置依据，不需要逐节课看视频
          </p>
        </div>

        {/* Dashboard mockup - 全宽仪表盘 */}
        <div className={`max-w-6xl mx-auto mb-10 rounded-2xl overflow-hidden transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* 顶部标题栏 */}
          <div className="bg-white border border-background-200/50 px-5 py-3 rounded-t-2xl flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-amber-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
            </div>
            <span className="text-xs font-mono text-foreground-950 ml-2">管理者仪表盘 · 质量概览</span>
          </div>

          {/* 主内容区域：背景网格 + 卡片 */}
          <div
            className="relative border-x border-b border-background-200/50 rounded-b-2xl p-6 md:p-10"
            style={{
              background: `
                linear-gradient(rgba(255,255,255,0.92), rgba(255,255,255,0.92)),
                linear-gradient(90deg, rgba(148,163,184,0.06) 1px, transparent 1px),
                linear-gradient(rgba(148,163,184,0.06) 1px, transparent 1px)
              `,
              backgroundSize: '100% 100%, 40px 40px, 40px 40px',
            }}
          >
            {/* 装饰数据流线条 */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.06]" preserveAspectRatio="none">
              <path d="M0 50 Q 300 20 600 80 T 1200 40" fill="none" stroke={VIVID.blue} strokeWidth="1.5" />
              <path d="M0 150 Q 400 100 800 180 T 1400 120" fill="none" stroke={VIVID.teal} strokeWidth="1" />
            </svg>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 relative z-10">
              {dashboardItems.map((item, i) => {
                const ChartComponent = ChartMap[item.chart];
                const theme = cardBgColors[i];
                return (
                  <div
                    key={item.label}
                    className="group relative rounded-xl border-2 backdrop-blur-md transition-all duration-500 hover:-translate-y-1 hover:shadow-lg overflow-hidden"
                    style={{
                      backgroundColor: theme.bg,
                      borderColor: theme.border,
                      transitionDelay: `${300 + i * 80}ms`,
                    }}
                  >
                    {/* 顶部色条 */}
                    <div className="h-2 w-full" style={{ backgroundColor: theme.top }} />

                    {/* 头部：图标 + 标题 + 指标 */}
                    <div className="p-5 pb-3 flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: theme.top }}
                        >
                          <i className={`${item.icon} text-base text-white`} />
                        </div>
                        <p className="text-sm font-body font-bold text-foreground-950">{item.label}</p>
                      </div>
                      <div className="text-right">
                        <span className="number-font text-lg font-bold leading-none" style={{ color: theme.top }}>
                          {item.metric}
                        </span>
                        <p className="text-[10px] text-foreground-950 font-body mt-0.5">{item.metricLabel}</p>
                      </div>
                    </div>

                    {/* 迷你数据图表 */}
                    <div className="px-5 pt-2 pb-1">
                      <ChartComponent />
                    </div>

                    {/* 底部描述 */}
                    <div className="px-5 pb-5 pt-1">
                      <div className="h-px w-full mb-3" style={{ backgroundColor: theme.border }} />
                      <p className="text-xs text-foreground-950 font-body leading-relaxed">{item.desc}</p>
                    </div>

                    {/* 悬浮装饰光点 */}
                    <div
                      className="absolute top-3 right-3 w-2 h-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{ backgroundColor: theme.top }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* 底部总结 */}
        <div className={`max-w-2xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="relative p-6 rounded-2xl glass-gold border-accent-200/30 overflow-hidden">
            {/* 背景装饰弧线 */}
            <svg className="absolute -right-6 -top-6 w-24 h-24 text-accent-200/20" viewBox="0 0 100 100">
              <path d="M50 0 A50 50 0 0 1 100 50" fill="none" stroke="currentColor" strokeWidth="8" />
            </svg>
            <p className="relative z-10 text-sm text-foreground-950 font-body leading-relaxed">
              管理者看到的<strong className="text-primary-700">不是单节课细节</strong>，而是<strong className="text-accent-700">一校一图、一省一图</strong>里的质量结构。
              这种能力，正是大规模常态化评价中<strong className="text-primary-700">人工方式最难完成</strong>的部分。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}